import classNames from "classnames";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import AvailableMinutesRangeItem from "PFApp/components/order/available_minutes_range_item";
import SortBy from "PFApp/components/sort_by";
import Toggle from "PFComponents/toggle/toggle";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { checkFiltersAreDefault, getValueFilters } from "../../use_filtered_collection/format_filters";
import css from "./filter_bar.module.scss";
import Search from "./search";

const DEFAULT_FILTERS_BY_TAB = {
  control_centre: {
    fields: {
      state: ["draft", "new"],
      template_key: ["audit_engagement"]
    }
  },
  exceptions: {
    fields: {
      state: ["draft", "new"],
      template_key: ["audit_engagement"],
      workflow_state: ["exception"]
    }
  },
  manual_interventions: {
    fields: {
      state: ["draft", "new"],
      template_key: ["audit_engagement"],
      workflow_state: ["shortlisting"]
    }
  }
};

const FilterBar = ({
  tab,
  data,
  onFilterChange,
  onFiltersReset,
  onOrderChange,
  noFiltersSelected,
  showPlanner,
  setShowPlanner,
  roleFilters
}) => {
  const stringify = useDeterministicStringify();
  const searchInputRef = useRef();
  const isSearchFocusedRef = useRef(false);
  const [showFilters, setShowFilters] = useState(false);
  const { t } = useTranslation("auditPlanner");
  useEffect(() => {
    setShowFilters(false);
  }, [tab]);

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const meta = useMemo(() => {
    const dataMeta = { ...data.meta };
    dataMeta.filters = {
      fields: {
        ...dataMeta.filters?.fields,
        ...roleFilters.fields
      },
      strings: {
        ...dataMeta.filters?.strings,
        ...roleFilters.strings
      },
      numbers: {
        ...dataMeta.filters?.numbers,
        ...roleFilters.numbers
      }
    };
    return dataMeta;
  }, [data, roleFilters]);

  const termFilter = meta.filters?.fields?.term;
  const orderMeta = meta.order;

  useEffect(() => {
    if (isSearchFocusedRef.current) {
      searchInputRef.current.focus();
    }
  }, [termFilter?.value]);

  const showGanttToggle = showPlanner !== undefined && typeof setShowPlanner === "function";
  const filtersAreClear =
    noFiltersSelected || checkFiltersAreDefault(getValueFilters(meta.filters), DEFAULT_FILTERS_BY_TAB[tab]);
  return (
    <div className={classNames(css.headerWrapper, { [css.plannerTab]: showPlanner })}>
      <div className={css.controls}>
        {showPlanner ? (
          <div /> // empty element to keep alignment
        ) : (
          <Search
            key={`${tab}+${termFilter?.value}`}
            ref={searchInputRef}
            term={termFilter?.value}
            onTermChange={(term) => onFilterChange(termFilter, term || "")}
            handleFocus={() => {
              if (!isSearchFocusedRef.current) {
                isSearchFocusedRef.current = true;
              }
            }}
            handleBlur={() => {
              if (isSearchFocusedRef.current) {
                isSearchFocusedRef.current = false;
              }
            }}
          />
        )}
        <div className={css.bottom}>
          {showGanttToggle && (
            <Toggle
              inline
              label={t("parts.plannerView")}
              checked={showPlanner}
              onChange={() => setShowPlanner(!showPlanner)}
            />
          )}
          {!showPlanner && orderMeta && orderMeta.options.length > 0 && !termFilter.value && (
            <SortBy
              options={orderMeta.options.map((option) => ({
                displayElement: option.text,
                id: option.text,
                item: option
              }))}
              sort={orderMeta.value?.text}
              onSortChange={onOrderChange}
            />
          )}
          <div className={css.filtersButton}>
            <FiltersButton
              filtersAreClear={filtersAreClear}
              filtersShown={showFilters}
              onClick={toggleFilters}
            />
          </div>
        </div>
      </div>
      {showFilters && (
        <div className={css.filters}>
          <FiltersBox
            style={{ position: "absolute", width: "100%", top: -45 }}
            blockedList={["sold", "chargeable", "availability"]}
            isLoading={data.loading}
            meta={meta}
            handleFilterChange={onFilterChange}
            clearFilters={onFiltersReset}
            filtersAreClear={filtersAreClear}
            closeFilters={() => setShowFilters(false)}
            clearRefValue={stringify(getValueFilters(meta.filters))}
            allowToClose={true}
          >
            {showPlanner && (
              <AvailableMinutesRangeItem
                style={{ margin: "0 30px", padding: "20px 10px" }}
                order={{ ...meta.order, title: "Most available in" }}
                handleChange={({ start_date, end_date }) => {
                  onOrderChange({ ...meta.order.value, start_date, end_date });
                }}
              />
            )}
          </FiltersBox>
        </div>
      )}
      <div
        className={classNames(css.filtersPreview, {
          [css.noFiltersSelected]: filtersAreClear,
          [css.engagementView]: !showPlanner
        })}
      >
        {!filtersAreClear && (
          <FiltersPreview
            meta={meta}
            blockedList={["term", "workflow_state", "state"]}
            filtersAreClear={false}
            clearFilters={onFiltersReset}
            onFilterChange={onFilterChange}
            onMoreClicked={() => setShowFilters(true)}
          />
        )}
      </div>
    </div>
  );
};

FilterBar.propTypes = {
  tab: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.object).isRequired,
  showPlanner: PropTypes.bool,
  setShowPlanner: PropTypes.func,
  data: PropTypes.shape({ meta: PropTypes.object, loading: PropTypes.bool }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onOrderChange: PropTypes.func.isRequired,
  onFiltersReset: PropTypes.func.isRequired,
  noFiltersSelected: PropTypes.bool.isRequired,
  roleFilters: PropTypes.array.isRequired
};

export default FilterBar;
